import { IUser } from '../types/damTypes';

type PopulatedRole = Exclude<IUser['roles'][number], string>;

const getPopulatedRoles = (user: IUser | null): PopulatedRole[] => {
  if (!user?.roles || !Array.isArray(user.roles)) return [];
  return user.roles.filter((role): role is PopulatedRole => typeof role !== 'string');
};

export const getUserPermissions = (user: IUser | null): Set<string> => {
  const userPermissions = new Set<string>();
  const populatedRoles = getPopulatedRoles(user);

  populatedRoles.forEach(role => {
    if (Array.isArray(role.permissions)) {
      role.permissions.forEach(permission => userPermissions.add(permission));
    }
  });

  return userPermissions;
};

/**
 * Checks if the user has the specified permission
 * @param user The current user object (with populated roles)
 * @param permission The permission to check for
 * @returns Boolean indicating if the user has the specified permission
 */
export const hasPermission = (user: IUser | null, permission: string): boolean => {
  if (!user) return false;

  const userPermissions = getUserPermissions(user);

  // Check if the user has the admin:all permission, which grants all access
  if (userPermissions.has('admin:all')) {
    return true;
  }

  // Otherwise check the specific permission
  return userPermissions.has(permission);
};

/**
 * Checks if the user has any of the specified permissions
 * @param user The current user object
 * @param permissions Array of permissions to check
 * @returns Boolean indicating if the user has any of the specified permissions
 */
export const hasAnyPermission = (user: IUser | null, permissions: string[]): boolean => {
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Checks if the user has all of the specified permissions
 * @param user The current user object
 * @param permissions Array of permissions to check
 * @returns Boolean indicating if the user has all of the specified permissions
 */
export const hasAllPermissions = (user: IUser | null, permissions: string[]): boolean => {
  return permissions.every(permission => hasPermission(user, permission));
};

export const isOnlyKontorUser = (user: IUser | null): boolean => {
  if (!user) return false;

  const userPermissions = getUserPermissions(user);
  if (userPermissions.has('admin:all')) {
    return false;
  }

  const populatedRoles = getPopulatedRoles(user);
  if (populatedRoles.length === 0) {
    return false;
  }

  return populatedRoles.every(role => role.onlyKontor === true);
};

export const canAccessProductComparison = (user: IUser | null): boolean => {
  return hasPermission(user, 'system:manageProductComparison') && !isOnlyKontorUser(user);
};